import {useEffect, useMemo, useState} from 'react'
import {useQuery} from 'react-query'
import {getConcessionariesAll} from '../../../../../../services/concessionariesService'
import {getPlantsAllSimple} from '../../../../../../services/plantsService'
import {getInvoicesOneFilter} from '../../../../../../services/invoices'
import {KTSVG, toAbsoluteUrl} from '../../../../../../../_metronic/helpers'
import {Loading} from '../../../../../components/Loadings/loading'
import {useToast} from '../../../../../components/Toats'
import {SelectGlobal} from '../../../../../components/SelectGlobal'
import {getConcessionaireImageSrc} from '../../../TableUnitConsumers/BoardUnitConsumers'
import {ListPlantsLinked} from '../listPlantsLinked'
import {FieldError, FieldLabel, FormSection} from '../FormSection'
import {StepV2} from './components/stepV2'
import {ModalAddUpdateEmail} from './components/ModalAddEditEmail'
import {ModalDeleteEmail} from './components/ModalDeleteEmail'
import styles from '../../modalUnitConsumer.module.css'

const STATUS_MON_COLORS: Record<string, string> = {
  MONITORADA: 'success',
  'NÃO MONITORADA': 'danger',
  'EM MANUTENÇÃO': 'info',
}

const StepModal = ({dataEmails, hasError, formik, id, show, refresh, codeEmail}: any) => {
  const {toggleToast} = useToast()
  const [invoiceFilter, setInvoiceFilter] = useState<any>(null)
  const [showPassword, setShowPassword] = useState(false)
  const [copiedEmail, setCopiedEmail] = useState<string | null>(null)
  const [showCreateAppModal, setShowCreateAppModal] = useState<boolean>(false)
  const [showCreateAppModalDelete, setShowCreateAppModalDelete] = useState<boolean>(false)
  const [myEmail, setMyEmail] = useState<any | null>(null)
  const [activeTab, setActiveTab] = useState<'plant' | 'automation'>('plant')

  const emailPrefix = codeEmail || id || ''
  const linkEmails = emailPrefix
    ? [`${emailPrefix}@solarvision.email`, `${emailPrefix}@solarvisionapp.com`]
    : []

  const handleCopyEmail = (email: string) => {
    navigator.clipboard
      .writeText(email)
      .then(() => {
        setCopiedEmail(email)
        setTimeout(() => setCopiedEmail(null), 3000)
      })
      .catch((err) => console.error('Erro ao copiar:', err))
  }

  const {data: concessionaries = [], isLoading: loadingConcessionaries} = useQuery<any[]>(
    ['getConcessionariesUnitConsumersModal', show],
    async () => await getConcessionariesAll(),
    {
      onError: () => {
        toggleToast('Erro ao buscar a lista de concessionarias! ', 'error')
      },
    }
  )

  const {data: plants = [], isLoading: loadingPlants} = useQuery<any[]>(
    ['getPlantsLinkedUnitConsumers', show, formik.values.idUnitBranch],
    async () =>
      await getPlantsAllSimple(
        `?unitBranch=${formik.values.idUnitBranch ? formik.values.idUnitBranch : ''}`
      ),
    {
      keepPreviousData: true,
      onError: () => {
        toggleToast('Erro ao buscar a lista de usinas. ', 'error')
      },
    }
  )

  const concessionarie = useMemo(
    () =>
      formik.values.idConcessionarie
        ? concessionaries.find((item: any) => item.id === formik.values.idConcessionarie)
        : null,
    [formik.values.idConcessionarie, concessionaries]
  )

  useEffect(() => {
    if (!show || !id) {
      setInvoiceFilter(null)
      return
    }
    getInvoicesOneFilter(id, new Date())
      .then(setInvoiceFilter)
      .catch((error) => console.log(error))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show])

  const isPremiumPlant =
    plants?.find((plant: any) => plant.id === formik.values.idPlant)?.plano === 'PREMIUM'
  const credentialsDisabled = !concessionarie || concessionarie.status_mon_aut === 'NÃO MONITORADA'

  if (loadingConcessionaries) {
    return (
      <div
        style={{display: 'flex', justifyContent: 'center', height: '15rem', alignItems: 'center'}}
      >
        <Loading myClass='spinner-border' style={{width: '3rem', height: '3rem'}} />
      </div>
    )
  }

  return (
    <>
      <div className={styles.grid}>
        <div className={styles.column}>
          <FormSection
            icon='/media/icons/duotune/general/gen001.svg'
            title='Identificação'
            description='Dados principais da unidade consumidora'
          >
            <div className={styles.fieldsGrid}>
              <div className={styles.fieldFull}>
                <FieldLabel htmlFor='uc_name' required hint='Nome para identificar a unidade'>
                  Nome
                </FieldLabel>
                <input
                  id='uc_name'
                  type='text'
                  className={`form-control form-control-solid ${
                    hasError && !formik.values.name ? 'is-invalid' : ''
                  }`}
                  placeholder='Ex.: Casa da praia'
                  value={formik.values.name ?? ''}
                  onChange={formik.handleChange('name')}
                  autoFocus
                />
                <FieldError show={hasError && !formik.values.name}>
                  Informe um nome para a unidade consumidora.
                </FieldError>
              </div>

              <div>
                <FieldLabel
                  htmlFor='uc_contrato'
                  required
                  hint='Número do contrato/instalação da unidade na concessionária'
                >
                  Nº do contrato
                </FieldLabel>
                <input
                  id='uc_contrato'
                  type='text'
                  className={`form-control form-control-solid ${
                    hasError && !formik.values.contrato ? 'is-invalid' : ''
                  }`}
                  placeholder='Ex.: 1234567'
                  value={formik.values.contrato ?? ''}
                  onChange={formik.handleChange('contrato')}
                />
                <FieldError show={hasError && !formik.values.contrato}>
                  O número do contrato é obrigatório.
                </FieldError>
              </div>

              <div>
                <FieldLabel htmlFor='uc_cpf_cnpj' hint='CPF ou CNPJ do titular da unidade'>
                  CPF/CNPJ
                </FieldLabel>
                <input
                  id='uc_cpf_cnpj'
                  type='text'
                  className='form-control form-control-solid'
                  placeholder='000.000.000-00'
                  value={formik.values.cpf_cnpj ?? ''}
                  onChange={formik.handleChange('cpf_cnpj')}
                />
              </div>
            </div>
          </FormSection>

          <FormSection
            icon='/media/icons/duotune/abstract/abs027.svg'
            title='Concessionária'
            description='Distribuidora de energia que atende a unidade'
          >
            <FieldLabel required>Concessionária</FieldLabel>
            <SelectGlobal
              itens={concessionaries}
              buttonAll={true}
              textSearch={'Buscar concessionária'}
              textButtonInit={'Selecione a concessionária'}
              labelKey='name'
              valueKey='id'
              typeIcon='badge'
              badgeKey='status_mon_aut'
              colorsBadge={STATUS_MON_COLORS}
              value={formik.values.idConcessionarie ? formik.values.idConcessionarie : ''}
              className='w-100'
              onChange={(value: string) => {
                formik.setFieldValue('idConcessionarie', value || null)
              }}
            />
            <FieldError show={hasError && !formik.values.idConcessionarie}>
              Informe uma concessionária para a unidade.
            </FieldError>

            {concessionarie && (
              <div className={styles.concessionaireCard}>
                <img
                  src={getConcessionaireImageSrc(concessionarie.link_imagem)}
                  alt={concessionarie.name}
                  className={styles.concessionaireLogo}
                />
                <div>
                  <div className={styles.concessionaireName}>{concessionarie.name}</div>
                  <div className={styles.badges}>
                    {concessionarie.status_mon_aut && (
                      <span
                        className={`badge badge-light-${
                          STATUS_MON_COLORS[concessionarie.status_mon_aut] || 'secondary'
                        } fs-8`}
                      >
                        {concessionarie.status_mon_aut}
                      </span>
                    )}
                    {concessionarie.type_monitor && (
                      <span className='badge badge-light fs-8'>
                        Monitoramento por{' '}
                        {concessionarie.type_monitor === 'EMAIL' ? 'e-mail' : 'credenciais'}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </FormSection>

          {isPremiumPlant && id && ['EMAIL', 'CREDENCIAIS'].includes(concessionarie?.type_monitor) && (
            <FormSection
              icon='/media/icons/duotune/communication/com011.svg'
              title='Acesso à concessionária'
              description='Meio pelo qual as faturas serão recebidas para o monitoramento'
            >
              {concessionarie.type_monitor === 'EMAIL' && (
                <>
                  <span className='text-gray-600 fs-7 d-block mb-3'>
                    Cadastre um dos e-mails abaixo no sistema da concessionária. Clique para copiar.
                  </span>
                  {linkEmails.map((email) => (
                    <button
                      key={email}
                      type='button'
                      className={styles.emailOption}
                      onClick={() => handleCopyEmail(email)}
                      title='Copiar e-mail'
                    >
                      <span>{email}</span>
                      <KTSVG
                        path={
                          copiedEmail === email
                            ? '/media/icons/duotune/arrows/arr085.svg'
                            : '/media/icons/duotune/general/gen054.svg'
                        }
                        className={`svg-icon-4 ${
                          copiedEmail === email ? 'svg-icon-success' : 'svg-icon-muted'
                        }`}
                      />
                    </button>
                  ))}

                  <label className='form-check form-check-custom form-check-solid mt-3'>
                    <input
                      className='form-check-input'
                      type='checkbox'
                      checked={!!formik.values.provided_email_concessionarie}
                      onChange={(e) =>
                        formik.setFieldValue('provided_email_concessionarie', e.target.checked)
                      }
                    />
                    <span className='form-check-label fw-semibold text-gray-700'>
                      Confirmo que o e-mail foi vinculado na concessionária
                    </span>
                  </label>

                  <div className={styles.subTitle}>
                    E-mail(s) para redirecionamento
                    <button
                      type='button'
                      className='btn btn-sm btn-light-primary py-1 px-3'
                      onClick={() => {
                        setMyEmail(null)
                        setShowCreateAppModal(true)
                      }}
                    >
                      <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-5' />
                      Adicionar
                    </button>
                  </div>

                  <div className={styles.emailList}>
                    {dataEmails && dataEmails.length > 0 ? (
                      dataEmails.map((email: any) => (
                        <div key={email.id} className={styles.emailRow}>
                          <span>{email.email || ''}</span>
                          <div className='d-flex gap-1'>
                            <button
                              type='button'
                              className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'
                              style={{width: '1.8rem', height: '1.8rem'}}
                              title='Editar e-mail'
                              onClick={() => {
                                setMyEmail(email)
                                setShowCreateAppModal(true)
                              }}
                            >
                              <KTSVG
                                path='/media/icons/duotune/art/art005.svg'
                                className='svg-icon-5'
                              />
                            </button>
                            <button
                              type='button'
                              className='btn btn-icon btn-bg-light btn-active-color-danger btn-sm'
                              style={{width: '1.8rem', height: '1.8rem'}}
                              title='Apagar e-mail'
                              onClick={() => {
                                setMyEmail(email)
                                setShowCreateAppModalDelete(true)
                              }}
                            >
                              <KTSVG
                                path='/media/icons/duotune/general/gen027.svg'
                                className='svg-icon-5'
                              />
                            </button>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className={styles.emptyBox}>
                        <KTSVG
                          path='/media/icons/duotune/communication/com011.svg'
                          className='svg-icon-2x svg-icon-muted'
                        />
                        Nenhum e-mail de redirecionamento cadastrado.
                      </div>
                    )}
                  </div>
                </>
              )}

              {concessionarie.type_monitor === 'CREDENCIAIS' && (
                <>
                  {credentialsDisabled && (
                    <span className='text-gray-600 fs-7 d-block mb-3'>
                      As credenciais ficam disponíveis quando a concessionária está monitorada.
                    </span>
                  )}
                  <div className={styles.fieldsGrid}>
                    <div>
                      <FieldLabel
                        htmlFor='uc_username'
                        hint='Pode ser o nome de usuário, CNPJ (XX.XXX.XXX/0001-XX), CPF (000.000.000-00) etc.'
                      >
                        Usuário
                      </FieldLabel>
                      <input
                        id='uc_username'
                        type='text'
                        className='form-control form-control-solid'
                        autoComplete='off'
                        disabled={credentialsDisabled}
                        value={formik.values.username ?? ''}
                        onChange={formik.handleChange('username')}
                      />
                    </div>
                    <div>
                      <FieldLabel
                        htmlFor='uc_password'
                        hint='Pode ser a senha, data de nascimento (00-00-0000) etc.'
                      >
                        Senha
                      </FieldLabel>
                      <div className='input-group input-group-solid'>
                        <input
                          id='uc_password'
                          type={showPassword ? 'text' : 'password'}
                          className='form-control form-control-solid'
                          autoComplete='new-password'
                          disabled={credentialsDisabled}
                          value={formik.values.password ?? ''}
                          onChange={formik.handleChange('password')}
                        />
                        <button
                          type='button'
                          className='input-group-text'
                          title={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                          onClick={() => setShowPassword((prev) => !prev)}
                        >
                          <i
                            className={`bi ${showPassword ? 'bi-eye-slash-fill' : 'bi-eye-fill'}`}
                            style={{fontSize: '1.1rem'}}
                          />
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </FormSection>
          )}

          <FormSection
            icon='/media/icons/duotune/finance/fin010.svg'
            title='Créditos e saldo'
            description='Créditos acumulados e saldo de economia da unidade'
          >
            {id && (
              <div className={styles.creditsStat}>
                <span className={styles.creditsValue}>
                  ~{invoiceFilter && invoiceFilter.creditos ? invoiceFilter.creditos : '0'}
                </span>
                <span className={styles.creditsUnit}>kWh</span>
                <span className={styles.creditsLabel}>Créditos acumulados</span>
              </div>
            )}
            <FieldLabel
              htmlFor='uc_saldo'
              hint='Saldo acumulado de economia anterior da unidade consumidora'
            >
              Saldo acumulado de economia anterior
            </FieldLabel>
            <input
              id='uc_saldo'
              type='text'
              inputMode='decimal'
              className='form-control form-control-solid'
              placeholder='0.00'
              value={formik.values.saldo_economizado_anterior ?? ''}
              onChange={formik.handleChange('saldo_economizado_anterior')}
            />
          </FormSection>
        </div>

        <div className={styles.columnRight}>
          <section className={`${styles.section} ${styles.tabsCard}`}>
            <div className={styles.tabs} role='tablist'>
              <button
                type='button'
                role='tab'
                aria-selected={activeTab === 'plant'}
                className={`${styles.tab} ${activeTab === 'plant' ? styles.tabActive : ''}`}
                onClick={() => setActiveTab('plant')}
              >
                <img
                  src={toAbsoluteUrl('/media/imgs/plant2.png')}
                  alt=''
                  className={styles.tabImage}
                />
                <span className={styles.required}>Usina vinculada</span>
                {hasError && !formik.values.idPlant && <span className={styles.tabErrorDot} />}
              </button>
              <button
                type='button'
                role='tab'
                aria-selected={activeTab === 'automation'}
                className={`${styles.tab} ${activeTab === 'automation' ? styles.tabActive : ''}`}
                onClick={() => setActiveTab('automation')}
              >
                <KTSVG path='/media/icons/duotune/ecommerce/ecm010.svg' className='svg-icon-3' />
                Automação das faturas
              </button>
            </div>

            <div className={styles.tabBody}>
              {activeTab === 'plant' ? (
                <>
                  <span className={styles.tabDescription}>
                    Usina que fornece os créditos para esta unidade
                  </span>
                  <ListPlantsLinked plants={plants} formik={formik} loading={loadingPlants} />
                  <FieldError show={hasError && !formik.values.idPlant}>
                    Selecione a usina à qual a unidade está vinculada.
                  </FieldError>
                </>
              ) : (
                <>
                  <span className={styles.tabDescription}>
                    Valores padrão usados nas faturas não informadas
                  </span>
                  <StepV2 formik={formik} />
                </>
              )}
            </div>
          </section>
        </div>
      </div>

      <ModalAddUpdateEmail
        show={showCreateAppModal}
        id={myEmail ? myEmail.id : undefined}
        data={myEmail ? myEmail : undefined}
        idUnitConsumer={id ? id : ''}
        handleClose={() => {
          setMyEmail(null)
          setShowCreateAppModal(false)
          refresh()
        }}
      />
      <ModalDeleteEmail
        show={showCreateAppModalDelete}
        data={myEmail}
        id={myEmail ? myEmail.id : undefined}
        handleClose={() => {
          setMyEmail(null)
          setShowCreateAppModalDelete(false)
          refresh()
        }}
      />
    </>
  )
}
export {StepModal}
